// inv/src/components/ui/GlobalPopup.jsx
import { useEffect, useState } from 'react';
import { submitTrafficSource } from '../../../service-scripts/popupApi';

const GlobalPopup = ({
  title,
  text,
  valid_until,
  cta,
  imageUrl,
  closeable = true,
  discountCode,
  storageKey,   // localStorage key
  onClose,
  onAnswered,   // ✅ new prop (optional)
}) => {
  if (!title && !text) return null;

  // 'intro' | 'question' | 'done' – just a string in JS
  const [step, setStep] = useState('intro');
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const answeredKey = storageKey ? `${storageKey}_answered` : null;

  // On first mount, check whether the user has already responded
  useEffect(() => {
    if (!answeredKey || typeof window === 'undefined') return;

    try {
      const raw = window.localStorage.getItem(answeredKey);
      if (raw === '1') {
        setSubmitted(true);
        setStep('done');
      }
    } catch (e) {
      console.warn('[GlobalPopup] localStorage read failed', e);
    }
  }, [answeredKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = answer.trim();

    if (!trimmed) {
      setError('Please answer the question to get your discount code.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await submitTrafficSource(trimmed);
      setSubmitted(true);
      setStep('done');

      // mark that the user has already received the code
      if (answeredKey && typeof window !== 'undefined') {
        window.localStorage.setItem(answeredKey, '1');
      }

      // ✅ notify the container that a response has been received
      if (typeof onAnswered === 'function') {
        onAnswered();
      }
    } catch (err) {
      console.error('[GlobalPopup] submitTrafficSource failed:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div
        className="
          relative
          w-[90vw] max-w-sm
          h-[420px] sm:h-[400px] max-h-[70vh]
          rounded-2xl
          overflow-hidden
          shadow-2xl
          transform transition-transform duration-300
          scale-95 sm:scale-100 translate-y-2 sm:translate-y-0
          border border-yellow-400/40
        "
      >
        {/* Background image (if available) */}
        {imageUrl && (
          <img
            src={imageUrl}
            alt={title || 'Popup background'}
            className="absolute inset-0 h-full w-full object-cover opacity-80"
          />
        )}

        {/* Dark overlay for text readability with a subtle gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/60 via-slate-900/90 to-zinc-950/95" />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col p-5 text-white">
          {closeable && (
            <button
              type="button"
              onClick={onClose}
              className="absolute cursor-pointer right-3 top-3 text-white/70 hover:text-white text-lg"
              aria-label="Close popup"
            >
              ×
            </button>
          )}

          <div className="mt-5 flex-1 flex flex-col">
            {/* Badge */}
            <span className="inline-flex items-center self-center rounded-full bg-yellow-400/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-yellow-300 mb-2">
              One quick question. Instant code.
            </span>

            {title && (
              <h2 className="text-xl font-semibold mb-2 text-center drop-shadow">
                {title}
              </h2>
            )}

            {text && (
              <p className="text-sm md:text-base text-white/85 mb-3 whitespace-pre-line text-center drop-shadow">
                {text}
              </p>
            )}
            {valid_until && (
              <p className="text-xs md:text-sm text-white/70 mb-3 text-center">
                Offer valid until <span className="font-semibold text-yellow-300">{valid_until}</span>.
              </p>
            )}

            {/* Step 1: intro — explain the flow before showing the question */}
            {step === 'intro' && !submitted && (
              <div className="mt-2 flex flex-col items-center gap-3 text-center">
                <p className="text-sm text-white/80 max-w-xs">
                  You’ll get an instant discount code after answering a single short question.
                  No email, no signup.
                </p>

                <button
                  type="button"
                  onClick={() => setStep('question')}
                  className="
                    cursor-pointer inline-flex items-center justify-center
                    px-4 py-2 rounded-full
                    bg-yellow-400 text-zinc-900
                    text-sm font-semibold
                    hover:bg-yellow-300
                    transition shadow-md
                  "
                >
                  Next
                </button>
              </div>
            )}

            {/* Step 2: show the question and input field */}
            {step === 'question' && !submitted && (
              <form onSubmit={handleSubmit} className="mt-2 flex flex-col gap-2">
                <div className="text-center space-y-1">
                  <label className="text-sm font-medium text-white/90">
                  Where did you first hear about Crino?
                  </label>
                  <p className="text-[13px] text-white/70">
                    Answer this question and we’ll show your discount code right away.
                  </p>
                </div>

                <input
                  type="text"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  className="
                    w-full rounded-full px-4 py-2 text-sm
                    text-zinc-900 bg-white/95
                    placeholder:text-zinc-400
                    focus:outline-none focus:ring-2 focus:ring-yellow-400/80
                  "
                  placeholder="Google search, LinkedIn, Friend..."
                  disabled={submitting}
                />

                {error && (
                  <p className="text-xs text-red-200">
                    {error}
                  </p>
                )}

                <div className="mt-1 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setStep('intro')}
                    className="
                      cursor-pointer inline-flex items-center gap-1
                      text-xs text-white/60 hover:text-white
                    "
                  >
                    <span className="leading-none">Back</span>
                  </button>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="
                      cursor-pointer inline-flex items-center justify-center
                      px-4 py-2 rounded-full
                      bg-yellow-400 text-zinc-900
                      text-sm font-semibold
                      hover:bg-yellow-300
                      disabled:opacity-60 disabled:cursor-not-allowed
                      transition shadow-md
                    "
                  >
                    {submitting ? 'Sending...' : 'Get my discount code'}
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: show the received code */}
            {step === 'done' && submitted && (
              <div className="mt-3 space-y-3 text-center">
                <p className="text-sm text-white/90">
                  Thanks for your answer! 🎉
                </p>

                {discountCode ? (
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/95 px-3 py-2 text-sm font-semibold shadow-md">
                    <span className="text-zinc-900">Your discount code:</span>
                    <span className="font-mono bg-zinc-900 text-yellow-300 px-2 py-1 rounded">
                      {discountCode}
                    </span>
                  </div>
                ) : (
                  <p className="text-xs text-white/80">
                    (No discount code configured yet)
                  </p>
                )}

                {/* ✅ Explanation of where and how to use the code + link to /pricing/ */}
                <p className="text-xs text-white/80 max-w-xs mx-auto">
                  Apply this code during checkout when choosing your plan.
                  You can review all plans on our{' '}
                  <a
                    href="/pricing/"
                    className="underline text-yellow-300 hover:text-yellow-200"
                  >
                    pricing page
                  </a>
                  .
                </p>

                {cta?.label && cta?.url && (
                  <div className="pt-1">
                    <a
                      href={cta.url}
                      className="
                        inline-flex items-center justify-center
                        px-4 py-2 rounded-full
                        bg-yellow-400 text-zinc-900
                        text-sm font-semibold
                        hover:bg-yellow-300
                        transition shadow-md
                      "
                    >
                      {cta.label}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalPopup;
